import React, { useEffect, useState } from 'react';
import { Sparkles, ArrowRight, Loader2, X } from 'lucide-react';
import { useUser } from '../../context/UserContext';

/**
 * WelcomeNamePrompt — progressive profile completion.
 *
 * Triggered automatically the FIRST time a phone-OTP signup lands inside
 * the authenticated app, if the user's `name` is empty. Single-field ask
 * with a soft "Skip for now" so the user is never trapped.
 *
 * Why we don't ask for name during signup:
 *   • Phone OTP signup goal = minimum fields = highest conversion
 *   • Name capture at moment of "welcome" feels natural, not interrogative
 *   • If user skips, we'll ask again on /profile (also non-blocking)
 *
 * Rules:
 *   • Shown ONLY if userData is hydrated AND name is blank
 *   • Auto-suppresses for the session if dismissed (sessionStorage)
 *   • Renders as a centred modal on a dim backdrop
 */
const SUPPRESS_KEY = 'eager_welcome_name_skipped';

export default function WelcomeNamePrompt() {
    const { userData, isLoading, updateUser } = useUser();
    const [open, setOpen] = useState(false);
    const [name, setName] = useState('');
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);

    // Decide whether to show the prompt on every userData change.
    useEffect(() => {
        if (isLoading || !userData) { setOpen(false); return; }

        const hasName = (userData.name || '').trim().length > 1;
        if (hasName) { setOpen(false); return; }

        // Skipped earlier this session? Hold off.
        try {
            if (sessionStorage.getItem(SUPPRESS_KEY) === '1') { setOpen(false); return; }
        } catch { /* private mode — ignore */ }

        setOpen(true);
    }, [userData, isLoading]);

    const skip = () => {
        try { sessionStorage.setItem(SUPPRESS_KEY, '1'); } catch { /* noop */ }
        setOpen(false);
    };

    const save = async (e) => {
        e?.preventDefault();
        const cleaned = name.trim();
        if (cleaned.length < 2) {
            setErr('Please enter your name (at least 2 characters).');
            return;
        }
        setErr(null); setBusy(true);
        try {
            await updateUser({ name: cleaned });
            setOpen(false);
        } catch (ex) {
            setErr(ex?.message || 'Could not save your name. Please try again.');
        } finally {
            setBusy(false);
        }
    };

    if (!open) return null;

    return (
        <div
            data-testid="welcome-name-modal"
            className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-slate-950/85 backdrop-blur-sm animate-fade-in"
            role="dialog"
            aria-labelledby="welcome-name-title"
        >
            <div className="relative bg-slate-900 border border-slate-700 rounded-3xl p-7 sm:p-9 shadow-2xl max-w-md w-full">
                {/* Soft skip in the corner — never trap the user */}
                <button
                    type="button"
                    onClick={skip}
                    aria-label="Skip"
                    data-testid="welcome-name-skip"
                    className="absolute top-4 right-4 w-8 h-8 inline-flex items-center justify-center rounded-full text-slate-500 hover:bg-slate-800 hover:text-white transition-colors"
                >
                    <X className="w-4 h-4" />
                </button>

                {/* Hero */}
                <div className="mb-6">
                    <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 bg-emerald-500/10 border border-emerald-500/20 rounded-full text-[10px] font-black uppercase tracking-[0.25em] text-emerald-300 mb-3">
                        <Sparkles className="w-3 h-3" /> Welcome aboard
                    </div>
                    <h2 id="welcome-name-title" className="text-2xl sm:text-3xl font-black text-white tracking-tight leading-tight mb-2">
                        What should we call you?
                    </h2>
                    <p className="text-sm text-slate-400 leading-relaxed">
                        Just your first name is fine. We'll personalise your dashboard, college matches and progress reports.
                    </p>
                </div>

                <form onSubmit={save} className="space-y-3">
                    <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="e.g. Rohan"
                        autoFocus
                        maxLength={60}
                        data-testid="welcome-name-input"
                        className="w-full bg-slate-950/60 border border-slate-700 rounded-xl px-4 py-3.5 text-base text-white font-medium placeholder:text-slate-500 focus:outline-none focus:ring-4 focus:ring-emerald-500/20 focus:border-emerald-500"
                    />
                    {err && (
                        <div className="text-xs text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2">
                            {err}
                        </div>
                    )}
                    <button
                        type="submit"
                        disabled={busy || name.trim().length < 2}
                        data-testid="welcome-name-submit"
                        className="w-full inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white font-bold rounded-xl py-3.5 text-sm transition-colors disabled:opacity-50"
                    >
                        {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : <>Continue <ArrowRight className="w-4 h-4" /></>}
                    </button>
                </form>

                <button
                    type="button"
                    onClick={skip}
                    data-testid="welcome-name-skip-link"
                    className="mt-4 mx-auto block text-xs text-slate-500 hover:text-slate-300 underline decoration-dotted underline-offset-4"
                >
                    Skip for now — I'll add it later
                </button>
            </div>
        </div>
    );
}
